import React from "react";
import { nowISO, computeAssessmentGrading } from "../../utils";
import Quiz from "../../pages/quiz";
import { useStudy } from "../../pages/study/hooks";
import {
  getAssessmentById,
  getQuestionsByAssessmentId,
} from "../../store/course/courseBaseApi";
import {
  addAssessmentSummary,
  addAssignmentActivitiesByCourseAndAssignee,
  addAssignmentProgress,
} from "../../store/study/studyBaseApi";
import { useQuiz, useQuizTimer } from "../../pages/quiz/hooks";
import { addAssessmentResultPreview } from "../../store/student/studentBaseApi";
import {
  getAssessmentResult,
  saveAssessmentResult,
} from "../../utils/storageHelper";
import { SectionCard } from "../study/course/SectionCard";
import { useCourse } from "../../pages/course/hooks";

export default function Assessment({
  data,
  sectionId,
  courseId,
  onComplete,
  mandatePassing = true,
  onNextChapter,
}) {
  const [chapter, setChapter] = React.useState({});
  const [assessment, setAssessment] = React.useState(null);
  const [loadError, setLoadError] = React.useState(null);
  const [assessmentId, setAssessmentId] = React.useState(data?.assessmentId);
  const [completed, setCompleted] = React.useState(false);
  const { setProgress, profile } = useStudy();
  const { lastSectionChapter } = useCourse();
  const {
    gradingResult,
    result,
    quizDetails,
    evaluationResult,
    setGradingResult,
    setEvaluationResult,
    updateCurrentCourseDetails,
  } = useQuiz();
  const { endTime: timerEndTime, endedAt } = useQuizTimer();

  // Guards so progress is persisted exactly once: `persistingRef` blocks concurrent
  // runs (auto-persist effect + Continue), `persistedRef` blocks re-runs after success.
  const persistingRef = React.useRef(false);
  const persistedRef = React.useRef(false);

  const onFail = () => {};

  // Persist the assessment progress (summary + activities + CHAPTER_COMPLETE + result
  // preview) for the given finalized `grading`. Persistence only — navigation stays on
  // the Continue handler. Runs once (guarded).
  // Retry the assessment-summary write specifically, since it is the one call that
  // populates ir_assignment_progress.assessment_summary. Returns true only if it lands,
  // so a transient failure can be retried on the next Continue/reload instead of being
  // silently lost. The other posts (progress/activities/preview) stay best-effort.
  const writeAssessmentSummary = async (summary, attempts = 3) => {
    for (let i = 0; i < attempts; i++) {
      try {
        await addAssessmentSummary({
          params: { courseId, assigneeId: profile.id },
          body: summary,
        });
        return true;
      } catch (e) {
        console.error(`addAssessmentSummary attempt ${i + 1} failed`, e);
        if (i < attempts - 1) {
          await new Promise((r) => setTimeout(r, 500 * (i + 1)));
        }
      }
    }
    return false;
  };

  const persistProgress = async (grading) => {
    if (persistedRef.current || persistingRef.current) return;
    if (!assessmentId || !grading) return;

    persistingRef.current = true;
    try {
      setCompleted(true);

      let startTime =
        new Date(grading.endedAt).getTime() - grading.timeTaken * 1000;
      try {
        startTime = new Date(startTime).toISOString();
      } catch (e) {
        console.error(e);
        startTime = nowISO();
      }

      let endTime = grading.endedAt;
      let params = {
          assessmentId: assessmentId,
          id: profile?.id,
        },
        body = result;

      let summary = {
          attendedOn: nowISO(),
          assessmentId,
          sectionId,
          chapterId: chapter.chapterId,
          timeTaken: Math.round(grading.timeTaken || 0), // seconds
          scored: grading.scored,
          total: grading.total,
          gradeAchieved: grading.grade,
        },
        activity = {
          assigneeId: profile.id,
          courseId,
          sectionId,
          chapterId: chapter.chapterId,
          assessmentId,
          endTime,
          startTime,
          type: "ASSESSMENT",
        };

      // The assessment-summary write is authoritative and retried; the rest stay
      // best-effort. We only consider this assessment fully persisted once the summary
      // lands, so a failed summary write gets retried later rather than lost.
      const summarySaved = await writeAssessmentSummary(summary);

      let settled = await Promise.allSettled([
        addAssignmentProgress({
          params: { courseId, assigneeId: profile.id },
          body: {
            sectionId,
            chapterId: chapter.chapterId,
            gradeAchieved: grading.grade,
            startTime,
            endTime,
          },
        }),
        addAssignmentActivitiesByCourseAndAssignee({
          params: { courseId, assigneeId: profile.id },
          body: {
            ...activity,
            assessmentScore: grading.activityScore || 0,
          },
        }),
        addAssignmentActivitiesByCourseAndAssignee({
          params: { courseId, assigneeId: profile.id },
          body: { ...activity, type: "CHAPTER_COMPLETE" },
        }),
        addAssessmentResultPreview({ params, body }),
      ]);

      let rejected = settled.filter((e) => e.status === "rejected");
      rejected.forEach((e) => console.error(e));

      if (summarySaved) {
        // Mark the cached result as backend-persisted so a reload short-circuits the
        // re-post (avoids duplicate summary entries), while a failed write leaves
        // persisted=false and gets retried.
        saveAssessmentResult(assessmentId, {
          evaluationResult,
          gradingResult: grading,
          result,
          persisted: true,
        });
        persistedRef.current = true;
      }
    } finally {
      persistingRef.current = false;
    }
  };

  // Persist progress the moment the assessment is evaluated (the "View Your Score"
  // screen, before the result page), and cache the result locally so a reload lands the
  // student back on the result page instead of restarting the quiz. `evaluationResult`
  // is empty until evaluation completes, so this fires once.
  React.useEffect(() => {
    if (persistedRef.current) return;
    if (!assessmentId) return;
    // `evaluated` is the only reliable completion signal — totalMarks is a primitive
    // double on the backend that defaults to 0.0, so a `totalMarks == null` check never
    // fires and can't distinguish "pending/failed" from a legitimate zero score.
    if (!evaluationResult || !evaluationResult.evaluated) return;
    if (!quizDetails || !quizDetails.gradingModel) return;

    const grading = computeAssessmentGrading({
      quizDetails,
      evaluationResult,
      endTime: timerEndTime,
      endedAt,
    });
    setGradingResult(grading);

    // Only short-circuit when a prior session actually persisted the summary to the
    // backend (persisted === true). A cached-but-unpersisted result (failed write) must
    // fall through so persistProgress retries it instead of leaving the column null.
    const cached = getAssessmentResult(assessmentId);
    if (cached && cached.persisted) {
      persistedRef.current = true;
      return;
    }

    // Cache the result so a reload rehydrates the result page. Mark persisted=false for
    // now; persistProgress flips it to true once the summary write succeeds.
    saveAssessmentResult(assessmentId, {
      evaluationResult,
      gradingResult: grading,
      result,
      persisted: false,
    });
    persistProgress(grading);
  }, [evaluationResult, quizDetails, assessmentId]);

  // Continue handler: ensure progress is persisted (no-op if already done), then navigate.
  const handleComplete = async () => {
    const grading =
      gradingResult && gradingResult.total != null
        ? gradingResult
        : computeAssessmentGrading({
            quizDetails,
            evaluationResult,
            endTime: timerEndTime,
            endedAt,
          });
    await persistProgress(grading);

    if (onComplete && typeof onComplete === "function") {
      onComplete();
    }

    if (assessmentId && !lastSectionChapter.chapter) {
      await onNextChapter();
    }
  };

  React.useEffect(() => {
    if (assessmentId) {
      setProgress({ assessment: assessmentId });
    }

    (async () => {
      setLoadError(null);
      let assessmentResp, questionsResp;
      try {
        [assessmentResp, questionsResp] = await Promise.all([
          getAssessmentById({ id: assessmentId }),
          getQuestionsByAssessmentId({ id: assessmentId }),
        ]);
      } catch (e) {
        console.error("[assessment] failed to load", assessmentId, e);
        setAssessment(null);
        setLoadError(e?.message || "Could not load this assessment.");
        return;
      }

      if (
        [typeof assessmentResp, typeof questionsResp].some(
          (v) => v === "undefined",
        )
      ) {
        return;
      }

      const _assessment = {
        ...assessmentResp.data,
        questions: questionsResp.questions,
      };

      setAssessment(_assessment);
    })();
  }, [assessmentId]);

  React.useEffect(() => {
    if (data && Object.keys(data).length) {
      setChapter(data);

      if (data.assessments && data.assessments.length) {
        const newAssessmentId = data.assessments[0].id;

        // A different chapter/assessment is loading. The Quiz and this
        // component are reused across chapters (no remount), so the
        // completion guards and the previous chapter's evaluation linger.
        // That made chapter 2 reuse chapter 1's result — the second
        // assessment auto-completed and got skipped. Reset everything that's
        // scoped to a single assessment so each chapter is evaluated and
        // persisted on its own. Guarded on an id change so it never wipes
        // state mid-assessment.
        if (newAssessmentId !== assessmentId) {
          persistedRef.current = false;
          persistingRef.current = false;
          setCompleted(false);
          setEvaluationResult({});
          setGradingResult({
            grade: null,
            passed: null,
            timeTaken: null,
            scored: null,
            total: null,
            activityScore: 0,
          });
        }

        setAssessmentId(newAssessmentId);
        updateCurrentCourseDetails({ assessmentId: newAssessmentId });
      }
    }
  }, [data]);

  return (
    <React.Fragment>
      {!assessment && loadError && (
        <SectionCard>
          <p style={{ margin: 0 }}>This assessment could not be loaded. Please try again later.</p>
          {process.env.NODE_ENV === "development" && (
            <p style={{ margin: "8px 0 0", color: "#888", fontSize: 12, wordBreak: "break-all" }}>{loadError}</p>
          )}
        </SectionCard>
      )}
      {assessment && (
        <SectionCard style={{ padding: 0 }}>
          <Quiz
            data={assessment}
            onFail={onFail}
            onComplete={handleComplete}
            mandateSpeechEvaluation={false}
            type={"assessment"}
          />
        </SectionCard>
      )}
    </React.Fragment>
  );
}
